import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, Award, BadgeCheck, Building2, CheckCircle2, Cpu,
  DollarSign, Factory, FileWarning, Flame, Globe2, Landmark, LayoutDashboard,
  RefreshCcw, ScrollText, Server, ShieldCheck, Timer, TrendingUp, Users, Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { EnvironmentChip } from "@/components/EnvironmentBanner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * THOM Verify™ Enterprise Operations Command Centre — single pane of glass.
 * 100% additive; consumes /command-centre/{overview,events,alerts}.
 */
export default function OperationsCommandCentre() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [country, setCountry] = useState("");
  const [environment, setEnvironment] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setRefreshing(true); setError(null);
    try {
      const [o, e, a] = await Promise.all([
        api.get("/command-centre/overview", { params: { country: country || undefined, environment: environment || undefined } }),
        api.get("/command-centre/events", { params: { limit: 40 } }),
        api.get("/command-centre/alerts"),
      ]);
      setData(o.data); setEvents(e.data.events || []); setAlerts(a.data.alerts || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, environment]);
  useEffect(() => { const t = setInterval(load, 45000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [country, environment]);

  const countries = useMemo(() => (data?.country_activity || []).map(c => ({ iso: c.iso, name: c.name })), [data]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="kicker flex items-center gap-2">
              Operations Command Centre <EnvironmentChip env={environment || "production"} testid="cc-env-chip" />
              {data?.kpis?.system_health && (
                <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${data.kpis.system_health === "healthy" ? "bg-emerald-100 text-emerald-900 border border-emerald-300" : data.kpis.system_health === "warning" ? "bg-amber-100 text-amber-900 border border-amber-300" : "bg-red-100 text-red-900 border border-red-300"}`} data-testid="cc-system-health">
                  System · {data.kpis.system_health}
                </span>
              )}
            </div>
            <h1 className="display text-3xl mt-1">THOM Verify™ Enterprise Operations</h1>
            <p className="text-[12.5px] text-muted-foreground mt-1">Real-time cockpit for executives, operations and compliance. Aggregated from existing modules — no duplicated data.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="text-[12px] border border-border rounded-sm px-2 py-1 bg-background" data-testid="cc-country-filter">
              <option value="">All countries</option>
              {countries.map(c => <option key={c.iso} value={c.iso}>{c.name} ({c.iso})</option>)}
            </select>
            <select value={environment} onChange={(e) => setEnvironment(e.target.value)} className="text-[12px] border border-border rounded-sm px-2 py-1 bg-background" data-testid="cc-env-filter">
              <option value="">All environments</option>
              <option value="production">Production</option>
              <option value="pilot">Pilot</option>
              <option value="sandbox">Sandbox</option>
              <option value="demonstration">Demonstration</option>
            </select>
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="cc-refresh">
              <RefreshCcw className={`h-3 w-3 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {error && <div className="p-4 border border-red-500 bg-red-50 text-red-900 mb-4">Load failed: {error}</div>}
        {!data && !error && <div className="p-16 text-center text-muted-foreground">Loading command centre…</div>}

        {data && (
          <>
            {/* Alert Centre */}
            {alerts.length > 0 && (
              <div className="mb-5 border border-red-500/40 bg-red-50 p-3" data-testid="cc-alerts">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-700" />
                  <div className="mono text-[10.5px] uppercase tracking-widest text-red-800">Alert Centre · {alerts.length} pinned</div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {alerts.map((a, i) => (
                    <div key={i} className={`p-2 border-l-4 ${a.severity === "critical" ? "border-red-600 bg-white" : "border-amber-500 bg-white"} flex items-center gap-2 text-[12px]`}>
                      <span className={`mono text-[9px] uppercase tracking-widest px-1 rounded-sm ${a.severity === "critical" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{a.severity}</span>
                      <span className="font-medium">{a.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
              {/* Left column — all rows */}
              <div className="space-y-5">
                {/* Row 1 · KPIs */}
                <section data-testid="cc-row-kpis">
                  <RowKicker Icon={LayoutDashboard} label="Executive KPIs · Today" />
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    <KpiCard Icon={Building2}   label="Active Organisations"  value={data.kpis.active_organisations}  to="/orgs" />
                    <KpiCard Icon={Globe2}      label="Active Countries"       value={data.kpis.active_countries}      to="/africa/countries" />
                    <KpiCard Icon={Activity}    label="Verifications Today"    value={data.kpis.verifications_today}   to="/admin/dashboard" />
                    <KpiCard Icon={Timer}       label="Pending Verifications"  value={data.kpis.pending_verifications} to="/admin/dashboard" />
                    <KpiCard Icon={Award}       label="Certificates Today"     value={data.kpis.certificates_today}    to="/admin/certificates" />
                    <KpiCard Icon={BadgeCheck}  label="Passports Today"        value={data.kpis.passports_today} />
                    <KpiCard Icon={Landmark}    label="Escrows Active"         value={data.kpis.escrows_active} />
                    <KpiCard Icon={Zap}         label="API Requests Today"     value={data.kpis.api_requests_today}    to="/admin/api" />
                    <KpiCard Icon={FileWarning} label="Fraud Alerts Today"     value={data.kpis.fraud_alerts_today}    critical={data.kpis.fraud_alerts_today > 0} />
                    <KpiCard Icon={DollarSign}  label="Revenue Today (USD)"    value={fmtUsd(data.kpis.revenue_today_usd)} />
                    <KpiCard Icon={Timer}       label="SLA Breaches"           value={data.kpis.sla_breaches}          critical={data.kpis.sla_breaches > 0} />
                    <KpiCard Icon={ShieldCheck} label="System Health"          value={data.kpis.system_health}          highlight={data.kpis.system_health === "healthy"} />
                  </div>
                </section>

                {/* Row 2 · Queues */}
                <section data-testid="cc-row-queues">
                  <RowKicker Icon={Activity} label="Operational Queues · Live" />
                  <div className="grid md:grid-cols-2 gap-3">
                    <Queue title="Verification Queue"        rows={data.queues.verification}         cols={["ref","status","country"]} />
                    <Queue title="Inspection Queue"          rows={data.queues.inspection}           cols={["id","status"]} />
                    <Queue title="Awaiting Approval"          rows={data.queues.awaiting_approval}    cols={["ref","status"]} />
                    <Queue title="Certificate Issuance"      rows={data.queues.certificate_issuance} cols={["certificate_number","status","environment"]} />
                    <Queue title="Escrow Queue"               rows={data.queues.escrow}                cols={["id","status","amount_usd"]} />
                    <Queue title="Partner Activation Queue"  rows={data.queues.partner_activation}   cols={["ref","state"]} />
                    <Queue title="Identity Verification"      rows={data.queues.identity_verification} cols={["ref","state"]} />
                    <Queue title="API Activation Requests"    rows={data.queues.api_activation}        cols={["ref","state"]} />
                  </div>
                </section>

                {/* Row 3 · Environment / Service Health */}
                <section data-testid="cc-row-health">
                  <RowKicker Icon={Server} label="Enterprise Health" />
                  <div className="grid md:grid-cols-4 gap-2 mb-3">
                    {Object.entries(data.environment_totals).map(([k, v]) => (
                      <div key={k} className="border border-border bg-card p-3">
                        <div className="flex items-center gap-2"><EnvironmentChip env={k} /><span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Assets</span></div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                          <div><div className="text-muted-foreground">Vehicles</div><div className="mono">{v.vehicles}</div></div>
                          <div><div className="text-muted-foreground">Certs</div><div className="mono">{v.certificates}</div></div>
                          <div><div className="text-muted-foreground">Orgs</div><div className="mono">{v.orgs}</div></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {Object.entries(data.service_health).map(([k, v]) => (
                      <div key={k} className={`border p-2 flex items-center gap-2 ${v.status === "critical" ? "border-red-500 bg-red-50" : v.status === "warning" ? "border-amber-500 bg-amber-50" : "border-emerald-500/40 bg-emerald-50"}`}>
                        <CheckCircle2 className={`h-4 w-4 ${v.status === "critical" ? "text-red-700" : v.status === "warning" ? "text-amber-700" : "text-emerald-700"}`} />
                        <div className="min-w-0">
                          <div className="text-[11.5px] font-medium capitalize">{k}</div>
                          <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{v.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Row 5 · Country Operations */}
                <section data-testid="cc-row-countries">
                  <RowKicker Icon={Globe2} label="Country Operations" />
                  <div className="border border-border bg-card overflow-auto">
                    <table className="w-full text-[12px]">
                      <thead className="bg-muted/50">
                        <tr>
                          <Th>Country</Th><Th>Verifications</Th><Th>Certs</Th><Th>Inspections</Th><Th>Fraud</Th><Th>Escrows</Th><Th>Revenue (USD)</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.country_activity.map(c => (
                          <tr key={c.iso} className="border-t border-border hover:bg-muted/20">
                            <Td><span className="mono uppercase tracking-widest text-[10px]">{c.iso}</span> {c.name}</Td>
                            <Td>{c.verifications}</Td><Td>{c.certificates}</Td><Td>{c.inspections}</Td>
                            <Td className={c.fraud > 0 ? "text-red-700 font-semibold" : ""}>{c.fraud}</Td>
                            <Td>{c.escrows}</Td>
                            <Td className="mono">{fmtUsd(c.revenue_usd)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Row 6 · Marketplace */}
                <section data-testid="cc-row-marketplace">
                  <RowKicker Icon={Factory} label="Marketplace Operations" />
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    <MiniStat label="Demo"       value={data.marketplace.demo_listings} />
                    <MiniStat label="Awaiting"   value={data.marketplace.awaiting_verification} />
                    <MiniStat label="Verified"   value={data.marketplace.verified_listings} />
                    <MiniStat label="Archived"   value={data.marketplace.archived_listings} />
                    <MiniStat label="Promoted"   value={data.marketplace.promoted_listings} />
                    <MiniStat label="Buyer reqs" value={data.marketplace.buyer_requests} />
                    <MiniStat label="Dealer reqs" value={data.marketplace.dealer_requests} />
                  </div>
                </section>

                {/* Row 7 · Partner Operations */}
                <section data-testid="cc-row-partners">
                  <RowKicker Icon={Users} label="Partner Operations" />
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="border border-border bg-card">
                      <div className="p-2 border-b border-border mono text-[10px] uppercase tracking-widest text-muted-foreground">Recently registered</div>
                      <div className="divide-y divide-border">
                        {(data.partners.recent || []).slice(0, 6).map((p) => (
                          <div key={p.id} className="p-2 flex items-center justify-between text-[12px]">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{p.organisation?.name || p.ref}</div>
                              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.state}</div>
                            </div>
                            <span className="text-[10px] text-muted-foreground">{shortTs(p.created_at)}</span>
                          </div>
                        ))}
                        {(data.partners.recent || []).length === 0 && <div className="p-4 text-[12px] text-muted-foreground">No partner applications yet.</div>}
                      </div>
                    </div>
                    <div className="border border-border bg-card p-3 grid grid-cols-2 gap-2 text-[12px]">
                      <KV k="Pending activation"  v={data.partners.pending_activation} />
                      <KV k="Sandbox users"       v={data.partners.sandbox_users} />
                      <KV k="Production partners" v={data.partners.production_partners} />
                      <KV k="Integration failures" v={data.partners.integration_failures} highlight={data.partners.integration_failures > 0} />
                      <div className="col-span-2 mt-2 pt-2 border-t border-border">
                        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Top consumers (24h)</div>
                        {(data.partners.top_consumers || []).slice(0, 3).map((t, i) => (
                          <div key={i} className="flex items-center justify-between"><span className="mono truncate">{t.organisation || "—"}</span><span className="mono">{t.requests}</span></div>
                        ))}
                        {(data.partners.top_consumers || []).length === 0 && <div className="text-muted-foreground">No API traffic yet.</div>}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Row 8 · Certificate Operations */}
                <section data-testid="cc-row-certificates">
                  <RowKicker Icon={Award} label="Certificate Operations" />
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    <MiniStat label="Issued total"     value={data.certificates.issued_total} />
                    <MiniStat label="Issued today"     value={data.certificates.issued_today} />
                    <MiniStat label="Renewals"         value={data.certificates.renewals} />
                    <MiniStat label="Revocations"      value={data.certificates.revocations} tone="warning" />
                    <MiniStat label="Counter-sigs"     value={data.certificates.counter_signatures} />
                    <MiniStat label="Passport links"   value={data.certificates.digital_passport_links} />
                    <MiniStat label="Government"       value={data.certificates.by_beneficiary?.government || 0} />
                    <MiniStat label="Court"            value={data.certificates.by_beneficiary?.court || 0} />
                    <MiniStat label="Bank"             value={data.certificates.by_beneficiary?.bank || 0} />
                    <MiniStat label="Insurance"        value={data.certificates.by_beneficiary?.insurance || 0} />
                  </div>
                </section>

                {/* Row 9 · Fraud Intelligence */}
                <section data-testid="cc-row-fraud">
                  <RowKicker Icon={Flame} label="Fraud Intelligence" />
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    <MiniStat label="Duplicate ID"     value={data.fraud.duplicate_identity} tone="warning" />
                    <MiniStat label="Duplicate VIN"    value={data.fraud.duplicate_vin} tone="warning" />
                    <MiniStat label="Tampering"        value={data.fraud.tampering} tone="warning" />
                    <MiniStat label="High risk"        value={data.fraud.high_risk} tone="critical" />
                    <MiniStat label="Insp. failures"   value={data.fraud.inspection_failures} tone="warning" />
                    <MiniStat label="Suspicious"        value={data.fraud.suspicious_behaviour} tone="warning" />
                    <MiniStat label="API abuse"        value={data.fraud.api_abuse} tone="warning" />
                  </div>
                  {(data.fraud.trending || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {data.fraud.trending.map((t, i) => (
                        <span key={i} className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-red-100 text-red-900 border border-red-300">{t.kind} · {t.count}</span>
                      ))}
                    </div>
                  )}
                </section>

                {/* Row 10 · SLA */}
                <section data-testid="cc-row-sla">
                  <RowKicker Icon={Timer} label="SLA Dashboard" />
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    <MiniStat label="Upcoming"         value={data.sla.upcoming} />
                    <MiniStat label="Overdue"          value={data.sla.overdue}    tone={data.sla.overdue > 0 ? "critical" : "default"} />
                    <MiniStat label="Cert expiring"    value={data.sla.expiring_certificates} tone={data.sla.expiring_certificates > 0 ? "warning" : "default"} />
                    <MiniStat label="Insp. deadlines" value={data.sla.inspection_deadlines} />
                    <MiniStat label="Escrow deadlines" value={data.sla.escrow_deadlines} />
                    <MiniStat label="Partner reviews" value={data.sla.partner_reviews} />
                    <MiniStat label="Notif. failures" value={data.sla.notification_failures} tone={data.sla.notification_failures > 0 ? "warning" : "default"} />
                  </div>
                </section>

                {/* Row 11 · Revenue */}
                <section data-testid="cc-row-revenue">
                  <RowKicker Icon={TrendingUp} label="Revenue Dashboard" />
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    <MiniStat label="Today"          value={fmtUsd(data.revenue.today_usd)} />
                    <MiniStat label="MTD"            value={fmtUsd(data.revenue.month_to_date_usd)} />
                    <MiniStat label="Verification"   value={fmtUsd(data.revenue.verification_usd)} />
                    <MiniStat label="Escrow"         value={fmtUsd(data.revenue.escrow_usd)} />
                    <MiniStat label="Partner"        value={fmtUsd(data.revenue.partner_usd)} />
                    <MiniStat label="Marketplace"    value={fmtUsd(data.revenue.marketplace_usd)} />
                  </div>
                  <div className="mt-2 text-[12px] text-muted-foreground">
                    Monthly growth vs prior month:
                    <span className={`ml-1 font-semibold ${data.revenue.monthly_growth_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>{data.revenue.monthly_growth_pct > 0 ? "+" : ""}{data.revenue.monthly_growth_pct}%</span>
                  </div>
                </section>

                {/* Row 12 · Audit */}
                <section data-testid="cc-row-audit">
                  <RowKicker Icon={ScrollText} label="Audit — Recent Events" />
                  <div className="border border-border bg-card overflow-auto">
                    <table className="w-full text-[11.5px]">
                      <thead className="bg-muted/50"><tr>
                        <Th>When</Th><Th>Actor</Th><Th>Role</Th><Th>Action</Th><Th>Target</Th>
                      </tr></thead>
                      <tbody>
                        {data.audit.map((a, i) => (
                          <tr key={i} className="border-t border-border">
                            <Td className="mono">{shortTs(a.ts)}</Td>
                            <Td className="mono truncate max-w-[140px]">{a.actor_id || "—"}</Td>
                            <Td>{a.actor_role || "—"}</Td>
                            <Td className="mono">{a.action}</Td>
                            <Td className="mono truncate max-w-[220px]">{a.target_type}{a.target_id ? ` · ${a.target_id}` : ""}</Td>
                          </tr>
                        ))}
                        {(data.audit || []).length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No recent audit events.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              {/* Right column — Live event stream + Quick Actions */}
              <aside className="space-y-4">
                <div className="border border-border bg-card">
                  <div className="p-2 border-b border-border flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-primary" />
                    <div className="mono text-[10px] uppercase tracking-widest">Live Event Stream</div>
                  </div>
                  <div className="max-h-[520px] overflow-y-auto divide-y divide-border" data-testid="cc-event-stream">
                    {events.length === 0 && <div className="p-4 text-[12px] text-muted-foreground">No recent events.</div>}
                    {events.map((e, i) => (
                      <div key={i} className="p-2 text-[11.5px]">
                        <div className="flex items-center gap-2">
                          <span className="mono text-[9.5px] uppercase tracking-widest px-1 rounded-sm bg-muted text-muted-foreground">{e.source}</span>
                          <span className="mono text-[10px] text-muted-foreground">{shortTs(e.ts || e.created_at)}</span>
                        </div>
                        <div className="mt-0.5 truncate">
                          {e.action || e.kind || e.state || "event"}
                          {e.ref && <span className="mono text-muted-foreground"> · {e.ref}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border border-border bg-card p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Quick Actions</div>
                  <div className="grid grid-cols-2 gap-2">
                    <QuickAction to="/admin/certificates" label="Issue Certificate" Icon={Award} />
                    <QuickAction to="/admin/onboarding"   label="Approve Partner"   Icon={Users} />
                    <QuickAction to="/admin/dashboard"    label="Assign Inspector"  Icon={Activity} />
                    <QuickAction to="/admin/escrow"       label="Release Escrow"    Icon={Landmark} />
                    <QuickAction to="/admin/fraud"        label="View Fraud Case"   Icon={FileWarning} />
                    <QuickAction to="/orgs"               label="Create Org"        Icon={Building2} />
                    <QuickAction to="/marketplace"        label="Marketplace"       Icon={Factory} />
                    <QuickAction to="/admin/intelligence" label="Executive Intel"   Icon={TrendingUp} />
                    <QuickAction to="/admin/situation"    label="Situation Room"    Icon={Zap} />
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

const RowKicker = ({ Icon, label }) => (
  <div className="flex items-center gap-2 mb-2">
    <Icon className="h-4 w-4 text-primary" />
    <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);
const KpiCard = ({ Icon, label, value, to, critical, highlight }) => {
  const cls = `p-3 border rounded-sm bg-card transition-colors ${critical ? "border-red-500 bg-red-50" : highlight ? "border-emerald-500/40" : "border-border hover:border-primary"}`;
  const body = (
    <>
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${critical ? "text-red-700" : "text-primary"}`} />
        <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
      </div>
      <div className={`text-2xl font-semibold mt-1 truncate ${critical ? "text-red-700" : ""}`}>{value}</div>
    </>
  );
  return to ? <Link to={to} className={cls}>{body}</Link> : <div className={cls}>{body}</div>;
};
const MiniStat = ({ label, value, tone }) => (
  <div className={`p-2 border rounded-sm ${tone === "critical" ? "border-red-500 bg-red-50" : tone === "warning" ? "border-amber-500/40 bg-amber-50" : "border-border bg-card"}`}>
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
    <div className="text-lg font-semibold mt-0.5">{value ?? "—"}</div>
  </div>
);
const Queue = ({ title, rows, cols }) => (
  <div className="border border-border bg-card">
    <div className="p-2 border-b border-border mono text-[10px] uppercase tracking-widest text-muted-foreground">{title} · {rows?.length || 0}</div>
    <div className="max-h-[220px] overflow-y-auto">
      {(!rows || rows.length === 0) && <div className="p-3 text-[12px] text-muted-foreground">Empty.</div>}
      {(rows || []).map((r, i) => (
        <div key={i} className="p-2 border-t border-border first:border-t-0 flex items-center gap-2 text-[11.5px]">
          {cols.map((c) => <span key={c} className="mono truncate">{fmtCol(r[c])}</span>)}
        </div>
      ))}
    </div>
  </div>
);
const Th = ({ children }) => <th className="text-left mono text-[9.5px] uppercase tracking-widest text-muted-foreground px-2 py-1.5">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-2 py-1.5 ${className}`}>{children}</td>;
const KV = ({ k, v, highlight }) => (
  <div className="grid grid-cols-[1fr_auto] gap-2 items-baseline">
    <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</span>
    <span className={`text-[13px] font-semibold ${highlight ? "text-red-700" : ""}`}>{v}</span>
  </div>
);
const QuickAction = ({ to, label, Icon }) => (
  <Link to={to} className="border border-border rounded-sm p-2 flex items-center gap-2 text-[11.5px] hover:border-primary hover:bg-primary/5" data-testid={`cc-qa-${to.replace(/\W+/g, "-")}`}>
    <Icon className="h-3.5 w-3.5" />
    <span className="truncate">{label}</span>
  </Link>
);
const fmtCol = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") { try { return v.name || v.id || JSON.stringify(v).slice(0, 40); } catch { return String(v); } }
  return String(v);
};
const fmtUsd = (n) => "$" + (Math.round((Number(n) || 0) * 100) / 100).toLocaleString();
const shortTs = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
